import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import keycode from '../../../constants/keycode';

import '../modal.scss';

const MODAL_ON_CLASS_NAME = 'modal-on';
const appNode = document.getElementById('root');
const bodyNode = document.body;

const focusableSelectors = [
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    '[tabindex="0"]',
];

class ModalItem extends React.Component {
    constructor(props) {
        super(props);

        this.contentRef = React.createRef();
        this.focusTrapAfter = React.createRef();
        this.focusTrapBefore = React.createRef();

        this.handleBackwardTab = this.handleBackwardTab.bind(this);
        this.handleForwardTab = this.handleForwardTab.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    componentDidMount() {
        if (this.props.isOpen) {
            this.onOpen();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isOpen && this.props.isOpen) {
            this.onOpen();
        }

        if (prevProps.isOpen && !this.props.isOpen) {
            this.onClose();
        }
    }

    componentWillUnmount() {
        if (this.focusTimer) {
            clearTimeout(this.focusTimer);
        }

        this.setFocusBeforeOpen();

        bodyNode.classList.remove(MODAL_ON_CLASS_NAME);

        document.removeEventListener('keydown', this.handleKeyDown);
    }

    onClose() {
        this.setFocusBeforeOpen();

        if (appNode) {
            appNode.removeAttribute('aria-hidden');
        }

        if (this.focusTimer) {
            clearTimeout(this.focusTimer);
        }

        document.removeEventListener('keydown', this.handleKeyDown);
    }

    onOpen() {
        this.focusedNodeBeforeOpen = document.activeElement;

        this.setFocusAfterOpen();

        if (appNode) {
            appNode.setAttribute('aria-hidden', 'true');
        }

        bodyNode.classList.add(MODAL_ON_CLASS_NAME);

        document.addEventListener('keydown', this.handleKeyDown);
    }

    getFocusableNodes() {
        const contentNode = this.contentRef.current;
        const focusableNodes = contentNode.querySelectorAll(focusableSelectors.join(', '));

        return [...focusableNodes];
    }

    setFocusFirstFocusable() {
        const focusableNodes = this.getFocusableNodes();

        if (focusableNodes.length > 0) {
            focusableNodes[0].focus();
        }
    }

    setFocusLastFocusable() {
        const focusableNodes = this.getFocusableNodes();

        if (focusableNodes.length > 0) {
            focusableNodes[focusableNodes.length - 1].focus();
        }
    }

    setFocusBeforeOpen() {
        const focusableNodeNames = ['a', 'button'];

        if (
            this.focusedNodeBeforeOpen &&
            focusableNodeNames.includes(this.focusedNodeBeforeOpen.nodeName.toLowerCase())
        ) {
            this.focusedNodeBeforeOpen.focus();
        }
    }

    setFocusAfterOpen() {
        const { initialFocusTarget } = this.props;

        if (this.focusTimer) {
            clearTimeout(this.focusTimer);
        }

        this.focusTimer = setTimeout(() => {
            const contentNode = this.contentRef.current;

            if (
                document.activeElement !== contentNode &&
                !contentNode.contains(document.activeElement)
            ) {
                if (initialFocusTarget === 'content') {
                    contentNode.focus();
                } else {
                    this.setFocusFirstFocusable();
                }
            }

            this.focusTimer = null;
        }, 1000);
    }

    handleKeyDown(event) {
        const eventKeyCode = event.which || event.keyCode || 0;

        switch (eventKeyCode) {
        case keycode.TAB:
            if (event.shiftKey) {
                this.handleBackwardTab(event);
            } else {
                this.handleForwardTab(event);
            }
            break;

        // no default
        }
    }

    handleBackwardTab(event) {
        const firstFocusableNode = this.focusTrapBefore.current;

        if (document.activeElement === firstFocusableNode) {
            event.preventDefault();
            this.setFocusFirstFocusable();
        }
    }

    handleForwardTab(event) {
        const lastFocusableNode = this.focusTrapAfter.current;

        if (document.activeElement === lastFocusableNode) {
            event.preventDefault();
            this.setFocusLastFocusable();
        }
    }

    render() {
        const {
            children,
            className,
            isOpen,
            ariaLabel,
            hasSelect,
        } = this.props;
        const modalClassName = classNames('modal', className);
        const dialogAria = {};
        if (ariaLabel) {
            dialogAria['aria-label'] = ariaLabel;
        } else {
            dialogAria['aria-labelledby'] = 'dialog-title';
        }

        /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
        /* tabindex="0" is used as focus traps to control focus scope */

        return (
            isOpen ?
                <div className={modalClassName}>
                    <div className="modal__overlay" />
                    <span
                        className="modal__focus-trap"
                        ref={this.focusTrapBefore}
                        tabIndex="0"
                    />
                    <div
                        className="modal__wrapper"
                        role="dialog"
                        {...dialogAria}
                    >
                        <div
                            className={hasSelect ? 'modal__select-container' : 'modal__container'}
                            role="document"
                            id="modal-content"
                            ref={this.contentRef}
                            tabIndex="-1"
                        >
                            <div className="modal__body">
                                {children}
                            </div>
                        </div>
                    </div>
                    <span
                        className="modal__focus-trap"
                        ref={this.focusTrapAfter}
                        tabIndex="0"
                    />
                </div>
                :
                null
        );

        /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
    }
}

ModalItem.propTypes = {
    ariaLabel: PropTypes.string,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    className: PropTypes.string,
    initialFocusTarget: PropTypes.oneOf([
        'content',
        'control',
    ]),
    isOpen: PropTypes.bool,
    hasSelect: PropTypes.bool,
};

ModalItem.defaultProps = {
    ariaLabel: '',
    children: null,
    className: '',
    initialFocusTarget: 'content',
    isOpen: false,
    hasSelect: false,
};

export default ModalItem;
